import * as RadixCheckbox from '@radix-ui/react-checkbox';
import classNames from 'classnames';
import { memo, useId } from 'react';

import { type Color } from '@air/api/types';
import { CheckIcon } from '@air/design-icons';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

import { type ColorToggleParams } from '~/components/Filters/ColorFilter/ColorFilterCard';
import { getContrastColor } from '~/components/v2/utils/filters/getContrastColor';

export type DefaultColorFilterControlProps = {
  colors: Color[];
  onClick: (params: ColorToggleParams) => void;
  selectedColors: string[];
};

export const DefaultColorFilterControl = memo(({ colors, onClick, selectedColors }: DefaultColorFilterControlProps) => {
  const id = useId();

  return (
    <div className="flex flex-col gap-2" data-testid="DEFAULT_COLOR_FILTER_CONTROL">
      <p className="ads:text-primary text-12 text-gray-12">Default colors</p>
      <div className="flex flex-wrap gap-1">
        {colors.map((color) => {
          const isSelected = selectedColors.includes(color.hex);
          const isWhite = color.name === 'white';

          return (
            <label key={color.hex} htmlFor={id}>
              <RadixCheckbox.Root
                className="flex size-6 items-center justify-center rounded-24 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 ring-0 ring-transparent ring-inset transition-colors ads:hover:bg-hover-subtle hover:bg-gray-4 ads:data-[state=checked]:border-ads-gray-10 data-[state=checked]:border-gray-10 ads:data-[state=checked]:ring-ads-gray-10 data-[state=checked]:ring-1 data-[state=checked]:ring-gray-10"
                checked={isSelected}
                id={id}
                data-testid="COLOR_FILTER_CHECKBOX"
                data-color-name={color.name}
                // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                onCheckedChange={(checked) => onClick({ color, isSelected: !!checked })}
              >
                <div
                  className={classNames(
                    'flex size-4 items-center justify-center rounded-full',
                    isWhite && 'border ads:border-ads-gray-5 border-gray-5',
                  )}
                  style={{ backgroundColor: color.hex }}
                >
                  <RadixCheckbox.Indicator>
                    <CheckIcon size="xs" style={{ color: getContrastColor(color.hex) }} />
                  </RadixCheckbox.Indicator>
                </div>
              </RadixCheckbox.Root>
              <VisuallyHidden>Select {color.name}</VisuallyHidden>
            </label>
          );
        })}
      </div>
    </div>
  );
});

DefaultColorFilterControl.displayName = 'DefaultColorFilterControl';
